import { CenterAlignedLoader } from '@medly-components/core';
import { FC, lazy, Suspense, useCallback, useContext, useEffect } from 'react';
import { Navigate, Outlet, Routes as ReactRoutes, Route, useLocation } from 'react-router-dom';

import { PageLayout } from 'components';
import {
    ACCOUNT_SETTINGS_ROUTE,
    ACCOUNT_VERIFIED_ROUTE,
    APPOINTMENTS_ROUTE,
    ASSIGN_MAP_ROUTE,
    CAPTURE_MAP_ROUTE,
    CAPTURE_PROFILE_ROUTE,
    ERROR_ROUTE,
    GEAR_ROUTE,
    GUIDES_ROUTE,
    HOME_ROUTE,
    LANDING_ROUTE,
    LOGIN_ROUTE,
    MAP_INFO_ROUTE,
    MAP_ONBOARDING,
    MEDICATIONS_ROUTE,
    MY_MAP_ROUTE,
    MY_PROFILE,
    ONBOARDING_LANDING,
    ORY_SETTINGS_ROUTE,
    RECOVER_ACCOUNT_ROUTE,
    REQUEST_MAP_ROUTE,
    RESET_PASSWORD_ROUTE,
    SIGN_UP_ROUTE,
    SYMPTOMS_ROUTE,
    USER_DETAILS,
    USER_MANAGEMENT_ROUTE,
    USER_MAP_ROUTE,
    VERIFICATION_ROUTE
} from 'constants/routes';

import { ActiveGearProtectedRoute } from 'components/common/ProtectedRoute/ActiveGearProtectedRoute';
import { AdminProtectedRoute } from 'components/common/ProtectedRoute/AdminProtectedRoute';
import { MapOnboardingProtectedRoute } from 'components/common/ProtectedRoute/MapOnboardingProtectedRoute';
import { RequestMapProtectedRoute } from 'components/common/ProtectedRoute/RequestMapProtectedRoute';
import { SessionProtectedRoute } from 'components/common/ProtectedRoute/SessionProtectedRoute';
import { MapOnboardingContextProvider } from 'context/MapOnboardingContext';
import { MapSearchContextProvider } from 'context/MapSearchContext';
import { SideNavContextProvider } from 'context/SideNavContext/SideNavContext';
import { UserContext } from 'context/UserContext';
import { useAnalytics } from 'hooks/useAnalytics/useAnalytics';
import { CapturingMapContextProvider } from 'pages/CaptureMap/CapturingMapContext';
import { GuideFiltersContextProvider } from 'pages/Guides/GuideFilters';
import { MapInfo, MyMap } from 'pages/Map';
import MapRenderer from 'pages/Map/MyMap/Map/components/MapRenderer';
import { UserDetailContextProvider } from 'pages/UserManagement/UserDetailContext/UserDetailContext';
import { AuthProtectedRoute, RefreshUser } from '../components/common/ProtectedRoute';
import { OnboardingJourneyProtectedRoute } from '../components/common/ProtectedRoute/OnboardingJourneyProtectedRoute';
import { QuestionnaireContextProvider } from '../context/QuestionnaireContext';
import Login from '../pages/Login';

const Landing = lazy(() => import('pages/Landing'));
const OnboardingLanding = lazy(() => import('pages/OnboardingLanding'));
const Symptoms = lazy(() => import('pages/Symptoms'));
const Medications = lazy(() => import('pages/Medications'));
const Appointments = lazy(() => import('pages/Appointments'));
const AppointmentForm = lazy(() => import('pages/AppointmentForm'));
const Map = lazy(() => import('pages/Map/MyMap/Map'));
const Gear = lazy(() => import('pages/Gear'));
const Signup = lazy(() => import('pages/Signup'));
const CaptureProfile = lazy(() => import('pages/CaptureProfile'));
const CaptureMap = lazy(() => import('pages/CaptureMap'));
const Verification = lazy(() => import('pages/Verification'));
const SelfRecoveryAccountAccountFlowScreen = lazy(() => import('pages/RecoverAccount/SelfRecoveryAccountFlowScreen'));
const AdminRecoveryAccountFlowScreen = lazy(() => import('pages/RecoverAccount/AdminRecoveryAccountFlowScreen'));
const OrySettings = lazy(() => import('pages/OrySettings'));
const Settings = lazy(() => import('pages/Settings'));
const Home = lazy(() => import('pages/Home'));
const AccountVerified = lazy(() => import('pages/AccountVerified'));
const UserManagement = lazy(() => import('pages/UserManagement'));
const UserDetails = lazy(() => import('pages/UserManagement/UserDetails'));
const MapTest = lazy(() => import('pages/Map/MyMap/MapTest'));
const MyProfile = lazy(() => import('pages/MyProfile'));
const UserMap = lazy(() => import('pages/UserManagement/UserMap'));
const PageNotFound = lazy(() => import('pages/PageNotFound'));
const AssignMap = lazy(() => import('pages/UserManagement/AssignMap'));
const Error = lazy(() => import('pages/Error'));
const Guides = lazy(() => import('pages/Guides'));
const GuideProfile = lazy(() => import('pages/Guides/GuideProfile'));
const MapQuestionnaire = lazy(() => import('pages/RequestMap'));
const MapOnboarding = lazy(() => import('pages/MapOnboarding'));

export const Routes: FC = () => {
    const location = useLocation();
    const { user } = useContext(UserContext);
    const { captureMantaCareEvent } = useAnalytics();

    const triggerRouteChangeEvent = useCallback(async () => {
        if (!user?.id) return;

        captureMantaCareEvent({ path: location.pathname + window.location.search, type: 'page', action: 'visited' });
    }, [user?.id, location.pathname]);

    useEffect(() => {
        triggerRouteChangeEvent();
    }, [triggerRouteChangeEvent]);

    return (
        <Suspense fallback={<CenterAlignedLoader />}>
            <ReactRoutes location={location}>
                <Route path={LANDING_ROUTE} element={<Landing />} />
                <Route path={LOGIN_ROUTE} element={<Login />} />
                <Route path={SIGN_UP_ROUTE} element={<Signup />} />
                <Route path={RESET_PASSWORD_ROUTE} element={<SelfRecoveryAccountAccountFlowScreen />} />
                <Route path={RECOVER_ACCOUNT_ROUTE} element={<AdminRecoveryAccountFlowScreen />} />
                <Route path={ORY_SETTINGS_ROUTE} element={<OrySettings />} />
                <Route
                    element={
                        <SessionProtectedRoute>
                            <Outlet />
                        </SessionProtectedRoute>
                    }
                >
                    <Route
                        path={VERIFICATION_ROUTE}
                        element={
                            <AuthProtectedRoute>
                                <Verification />
                            </AuthProtectedRoute>
                        }
                    />
                    <Route
                        element={
                            <OnboardingJourneyProtectedRoute>
                                <Outlet />
                            </OnboardingJourneyProtectedRoute>
                        }
                    >
                        <Route
                            path={ONBOARDING_LANDING}
                            element={
                                <RefreshUser>
                                    <OnboardingLanding />
                                </RefreshUser>
                            }
                        />
                        <Route path={CAPTURE_PROFILE_ROUTE} element={<CaptureProfile />} />
                        <Route path={ACCOUNT_VERIFIED_ROUTE} element={<AccountVerified />} />
                        <Route
                            path={REQUEST_MAP_ROUTE}
                            element={
                                <RefreshUser>
                                    <RequestMapProtectedRoute>
                                        <Outlet />
                                    </RequestMapProtectedRoute>
                                </RefreshUser>
                            }
                        >
                            <Route
                                index
                                element={
                                    <QuestionnaireContextProvider>
                                        <MapQuestionnaire />
                                    </QuestionnaireContextProvider>
                                }
                            />
                        </Route>
                    </Route>
                    <Route
                        path={CAPTURE_MAP_ROUTE}
                        element={
                            <RefreshUser>
                                <CapturingMapContextProvider>
                                    <CaptureMap />
                                </CapturingMapContextProvider>
                            </RefreshUser>
                        }
                    />
                    <Route
                        element={
                            <OnboardingJourneyProtectedRoute>
                                <MapSearchContextProvider>
                                    <SideNavContextProvider>
                                        <PageLayout />
                                    </SideNavContextProvider>
                                </MapSearchContextProvider>
                            </OnboardingJourneyProtectedRoute>
                        }
                    >
                        <Route path={ACCOUNT_SETTINGS_ROUTE} element={<Settings />} />
                        <Route
                            path={APPOINTMENTS_ROUTE}
                            element={
                                <ActiveGearProtectedRoute gear={'appointments'}>
                                    <Outlet />
                                </ActiveGearProtectedRoute>
                            }
                        >
                            <Route index element={<Appointments />} />
                            <Route path={'new'} element={<AppointmentForm />} />
                            <Route path={':id'} element={<AppointmentForm />} />
                        </Route>
                        <Route
                            path={GUIDES_ROUTE}
                            element={
                                <GuideFiltersContextProvider>
                                    <Outlet />
                                </GuideFiltersContextProvider>
                            }
                        >
                            <Route index element={<Guides />} />
                            <Route path={':id'} element={<GuideProfile />} />
                        </Route>
                        <Route
                            path={SYMPTOMS_ROUTE}
                            element={
                                <ActiveGearProtectedRoute gear={'symptoms'}>
                                    <Outlet />
                                </ActiveGearProtectedRoute>
                            }
                        >
                            <Route index element={<Symptoms />} />
                            <Route path={'new'} element={<Symptoms />} />
                            <Route path={':id'} element={<Symptoms />} />
                        </Route>
                        <Route
                            path={MEDICATIONS_ROUTE}
                            element={
                                <ActiveGearProtectedRoute gear={'medications'}>
                                    <Outlet />
                                </ActiveGearProtectedRoute>
                            }
                        >
                            <Route index element={<Medications />} />
                            <Route path={'new'} element={<Medications />} />
                            <Route path={':id'} element={<Medications />} />
                        </Route>
                        <Route
                            path={MAP_ONBOARDING}
                            element={
                                <RefreshUser>
                                    <MapOnboardingContextProvider>
                                        <MapOnboardingProtectedRoute>
                                            <MapOnboarding />
                                        </MapOnboardingProtectedRoute>
                                    </MapOnboardingContextProvider>
                                </RefreshUser>
                            }
                        />
                        <Route path={GEAR_ROUTE} element={<Gear />} />
                        <Route
                            path={HOME_ROUTE}
                            element={
                                <RefreshUser>
                                    <Home />
                                </RefreshUser>
                            }
                        />
                        <Route
                            path={MY_MAP_ROUTE}
                            element={
                                <RefreshUser>
                                    <Outlet />
                                </RefreshUser>
                            }
                        >
                            <Route index element={<MyMap />} />
                            <Route path={':region'} element={<Map />}>
                                <Route index element={<Navigate to={MY_MAP_ROUTE} />} />
                                <Route path={':zoneName'} element={<MapRenderer />} />
                            </Route>
                        </Route>
                        <Route path={MAP_INFO_ROUTE} element={<MapInfo />} />
                        <Route
                            path={MY_PROFILE}
                            element={
                                <RefreshUser>
                                    <MyProfile />
                                </RefreshUser>
                            }
                        />
                        <Route
                            element={
                                <AdminProtectedRoute>
                                    <Outlet />
                                </AdminProtectedRoute>
                            }
                        >
                            <Route path={USER_MANAGEMENT_ROUTE} element={<UserManagement />} />
                            <Route
                                path={USER_DETAILS + '/:userId'}
                                element={
                                    <UserDetailContextProvider>
                                        <Outlet />
                                    </UserDetailContextProvider>
                                }
                            >
                                <Route index element={<UserDetails />} />
                                <Route path={USER_MAP_ROUTE} element={<UserMap />} />
                                <Route path={ASSIGN_MAP_ROUTE} element={<AssignMap />} />
                            </Route>
                        </Route>
                    </Route>
                    <Route path="/map" element={<Navigate to={MY_MAP_ROUTE} />} />
                    <Route path={ERROR_ROUTE} element={<Error />} />
                    <Route path={'*'} element={<PageNotFound />} />
                </Route>
                <Route element={<PageLayout />}>
                    <Route path={'/map-test'} element={<MapTest />} />
                </Route>
            </ReactRoutes>
        </Suspense>
    );
};
